import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  Fade,
  keyframes
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Mic as MicIcon,
  LocationOn as LocationIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

// Animation pulse pour la mise en évidence
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.02); opacity: 0.95; }
`;

// Animation glow pour le focus
const glow = keyframes`
  0%, 100% { box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1); }
  50% { box-shadow: 0 12px 40px rgba(33, 150, 243, 0.3); }
`;

const EnhancedCentralSearchBar = ({ compact = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // États locaux
  const [query, setQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  // Refs
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialisation de la reconnaissance vocale
  React.useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'fr-FR';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  // Gestion de la recherche vocale
  const handleVoiceSearch = () => {
    if (!recognitionRef.current) return;
    
    if (!isListening) {
      setIsListening(true);
      recognitionRef.current.start();
    } else {
      recognitionRef.current.stop();
    }
  };

  // Gestion du changement de texte
  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  // Gestion du focus
  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  // Gestion de la recherche
  const handleSearch = useCallback(() => {
    if (query.trim()) {
      // Redirection vers la page de recherche avec la requête
      const searchParams = new URLSearchParams({
        q: query.trim()
      });
      navigate(`/search?${searchParams.toString()}`);
    }
  }, [query, navigate]);

  // Gestion de la touche Entrée
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  // Nettoyage du champ
  const handleClear = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: compact ? '100%' : 800,
        mx: 'auto',
        mt: 0,
        mb: compact ? 0 : 6,
        p: compact ? { xs: 2, md: 2.5 } : { xs: 2.5, md: 3 },
        borderRadius: 4,
        position: 'relative',
        zIndex: 10,
        backgroundColor: 'transparent',
        backdropFilter: 'blur(10px)',
        border: 'none',
        boxShadow: 'none'
      }}
    >
      {/* Titre inspirant */}
      {!compact && (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <AutoAwesomeIcon sx={{ color: theme.palette.primary.main }} />
            {t('search.enhanced.title', 'Recherche Intelligente')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'white',
              opacity: 0.9,
              fontWeight: 500
            }}
          >
            {t('search.enhanced.subtitle', 'Découvrez exactement ce que vous cherchez')}
          </Typography>
        </Box>
      )}

      {/* Barre de recherche principale */}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          ref={inputRef}
          fullWidth
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={t('search.enhanced.placeholder', 'Recherchez un appartement, une maison, un équipement...')}
          variant="outlined"
          size={isMobile ? 'medium' : 'large'}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
              backgroundColor: theme.palette.background.paper,
              boxShadow: isFocused 
                ? `0 0 0 4px ${theme.palette.primary.main}20` 
                : '0 6px 24px rgba(0, 0, 0, 0.1)',
              border: `2px solid ${isFocused ? theme.palette.primary.main : 'transparent'}`,
              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                border: `2px solid ${theme.palette.primary.main}40`,
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 4px ${theme.palette.primary.main}20`,
                border: `2px solid ${theme.palette.primary.main}`,
              }
            },
            '& .MuiOutlinedInput-input': {
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: 500,
              padding: theme.spacing(isMobile ? 1.5 : 1.8, 1),
              color: theme.palette.text.primary
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon 
                  sx={{ 
                    color: 'primary.main',
                    fontSize: isMobile ? 28 : 32
                  }} 
                />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Bouton de recherche vocale — masqué sur mobile */}
                  {!isMobile && (
                    <Tooltip title={t('search.voice.tooltip', 'Recherche vocale')}>
                      <IconButton
                        onClick={handleVoiceSearch}
                        disabled={!recognitionRef.current}
                        sx={{
                          color: isListening ? 'error.main' : 'text.secondary',
                          animation: isListening ? 'pulse 1.5s infinite' : 'none',
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.1)' },
                            '100%': { transform: 'scale(1)' }
                          }
                        }}
                      >
                        <MicIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Bouton de nettoyage */}
                  {query && (
                    <Fade in timeout={200}>
                      <IconButton
                        onClick={handleClear}
                        size="small"
                        sx={{ color: 'text.secondary' }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </Fade>
                  )}

                  {/* Bouton de recherche principal */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    disabled={!query.trim()}
                    sx={{
                      ml: 1,
                      minWidth: isMobile ? 80 : 110,
                      height: isMobile ? 38 : 42,
                      borderRadius: 3,
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      fontWeight: 600,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                      boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)`,
                        transition: 'left 0.6s ease',
                      },
                      '&:hover': {
                        background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.primary.main} 70%, ${theme.palette.secondary.dark} 100%)`,
                        boxShadow: '0 8px 35px rgba(33, 150, 243, 0.6)',
                        transform: 'translateY(-2px) scale(1.01)',
                        '&::before': {
                          left: '100%',
                        }
                      },
                      '&:active': {
                        transform: 'translateY(-1px) scale(0.99)',
                        transition: 'all 0.1s ease',
                      },
                      '&:disabled': {
                        background: theme.palette.action.disabledBackground,
                        boxShadow: 'none',
                        transform: 'none',
                        '&::before': {
                          display: 'none'
                        }
                      }
                    }}
                  >
                    {t('search.enhanced.searchButton', 'Rechercher')}
                  </Button>
                </Box>
              </InputAdornment>
            )
          }}
        />

        {/* Indicateur de recherche vocale active */}
        {isListening && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: theme.zIndex.tooltip,
              backgroundColor: 'error.main',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 2,
              fontSize: '0.875rem',
              fontWeight: 500,
              animation: `${pulse} 1s ease-in-out infinite`
            }}
          >
            🎤 {t('search.voice.listening', 'Parlez maintenant...')}
          </Box>
        )}
      </Box>

      {/* Texte d'aide/exemples */}
      {!compact && !isFocused && !query && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontStyle: 'italic'
            }}
          >
            {t('search.enhanced.examples', 'Ex: "Appartement 3 pièces Alger", "Studio meublé Oran", "Maison avec jardin"')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default EnhancedCentralSearchBar;
